import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { useRef } from 'react';
import { useI18n } from '../../i18n';

interface TopDeviceEnergy {
  deviceId: number;
  deviceName: string;
  totalEnergy: number;
  transactionCount?: number;
}

interface TopDevicesEnergyChartProps {
  data: TopDeviceEnergy[];
  loading?: boolean;
}

function TopDevicesEnergyChart({ data, loading }: TopDevicesEnergyChartProps) {
  const chartRef = useRef<HighchartsReact.RefObject>(null);
  const { t } = useI18n();

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 h-80 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  // Check if we have energy data or only transaction counts
  const hasEnergyData = data.some(d => d.totalEnergy > 0);

  // Sort by energy or transactions and take top 5
  const sortedData = [...data].sort((a, b) => {
    if (hasEnergyData) return b.totalEnergy - a.totalEnergy;
    return (b.transactionCount || 0) - (a.transactionCount || 0);
  }).slice(0, 5);

  const categories = sortedData.map(d => d.deviceName);
  const chartValues = sortedData.map((d, index) => {
    const colors = ['#059669', '#10b981', '#34d399', '#6ee7b7', '#a7f3d0'];
    return {
      y: hasEnergyData ? d.totalEnergy : (d.transactionCount || 0),
      color: colors[index] || '#10b981',
    };
  });

  const chartTitle = hasEnergyData ? t('leg2_top5_consumption_per_charger') : t('leg2_top5_transactions_per_charger');
  const chartSubtitle = hasEnergyData ? t('leg2_most_energy_chargers') : t('leg2_most_sessions_chargers');
  const yAxisTitle = hasEnergyData ? t('leg2_energy_kwh') : t('leg2_transactions');
  const tooltipFormat = hasEnergyData 
    ? `${t('eq_consumption')}: <b>{point.y:.2f}</b> kWh`
    : `${t('leg2_transactions')}: <b>{point.y}</b>`;
  const dataLabelFormat = hasEnergyData ? '{point.y:.1f}' : '{point.y}';

  const options: Highcharts.Options = {
    chart: {
      type: 'column',
      height: 300,
      style: {
        fontFamily: 'inherit',
      },
    },
    title: {
      text: chartTitle,
      style: {
        fontSize: '16px',
        fontWeight: '600',
        color: '#1f2937',
      },
    },
    subtitle: {
      text: chartSubtitle,
      style: {
        fontSize: '12px',
        color: '#6b7280',
      },
    },
    xAxis: {
      categories,
      labels: {
        style: {
          fontSize: '11px',
          color: '#374151',
        },
        rotation: -30,
      },
      lineColor: '#e5e7eb',
    },
    yAxis: {
      min: 0,
      title: {
        text: yAxisTitle,
        style: {
          color: '#6b7280',
        },
      },
      labels: {
        style: {
          fontSize: '11px',
          color: '#6b7280',
        },
        format: '{value}',
      },
      gridLineColor: '#f3f4f6',
    },
    tooltip: {
      headerFormat: '<b>{point.key}</b><br/>',
      pointFormat: tooltipFormat,
      backgroundColor: 'rgba(255, 255, 255, 0.95)',
      borderColor: '#10b981',
      borderRadius: 8,
    },
    legend: {
      enabled: false,
    },
    plotOptions: {
      column: {
        borderRadius: 6,
        dataLabels: {
          enabled: true,
          format: dataLabelFormat,
          style: {
            fontSize: '10px',
            color: '#374151',
            textOutline: 'none',
          },
        },
      },
    },
    series: [{
      type: 'column',
      name: hasEnergyData ? t('eq_consumption') : t('leg2_transactions'),
      data: chartValues,
    }],
    credits: {
      enabled: false,
    },
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
      <HighchartsReact
        highcharts={Highcharts}
        options={options}
        ref={chartRef}
      />
      {data.length === 0 && !loading && (
        <div className="text-center text-gray-500 py-4">
          {t('leg2_no_data_available')}
        </div>
      )}
    </div>
  );
}

export default TopDevicesEnergyChart;
